import {useEffect, useState, type Ref} from 'preact/hooks';
import {Height} from 'react-animate-height';

/**
 * Custom hook that returns the height of a DOM element referenced by a ref object.
 * The height is updated whenever the DOM element resizes.
 * The height is initially set to 'auto'.
 * This is intended to be used in conjunction with the AnimateHeight component.
 *
 * @param ref - The ref object that references the DOM element.
 * @returns The height of the DOM element.
 * @example Animate the height of a container when its content changes.
 * ```ts
 * const ref = useRef<HTMLDivElement | null>(null);
 * const height = useHeight(ref);
 *
 * return (
 *  <AnimateHeight height={height}>
 *   <div ref={ref}>
 *    <div>Content</div>
 *   </div>
 *  </AnimateHeight>
 * );
 * ```
 */
export function useHeight(ref: Ref<HTMLElement | null>) {
  const [height, setHeight] = useState<Height>('auto');

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      setHeight(element.clientHeight);
    });

    if (element) {
      resizeObserver.observe(element);
    }

    return () => resizeObserver.disconnect();
  }, [ref]);

  return height;
}
